/**
 * Server functions for the finish-account page. They take no auth middleware:
 * the account comes from the httpOnly `account_link` cookie an emailed link
 * set, the signed-in session, or the httpOnly trial handoff cookie of the browser
 * that started a signed-out trial, and never from the request body.
 */

import { createServerFn, createServerOnlyFn } from "@tanstack/react-start";
import { z } from "zod";

import { authMiddleware } from "~/lib/auth-middleware";
import type { AccountReference } from "~/lib/server/account-finish";
import { finishAccountDestination } from "~/lib/shared/account-finish";

// Only handler bodies are compiled out of the client build, so this shared
// helper's server imports need the server-only wrapper to stay out of it.
const readAccountReference = createServerOnlyFn(
  async (): Promise<AccountReference> => {
    const { getCookie } = await import("@tanstack/react-start/server");
    const { COOKIE_NAMES } = await import("~/lib/cookies");
    const token = getCookie(COOKIE_NAMES.accountLink) ?? null;
    if (token) return { token };

    const [{ auth }, { rethrowClerkAuthError }, { getUserByClerkId }] =
      await Promise.all([
        import("@clerk/tanstack-react-start/server"),
        import("~/lib/auth/clerk-handshake"),
        import("~/lib/server/user"),
      ]);
    const { userId } = await auth().catch(rethrowClerkAuthError);
    const user = userId ? await getUserByClerkId(userId) : null;
    if (user) return { token: null, sessionUserId: user.id };
    return {
      token: null,
      handoffNonce: getCookie(COOKIE_NAMES.publicTrialHandoff) ?? null,
    };
  },
);

const nextInput = z.object({ next: z.string().max(2048) });

export const getFinishAccountStateFn = createServerFn({
  method: "GET",
}).handler(async () => {
  const { getFinishAccountState } = await import("~/lib/server/account-finish");
  return getFinishAccountState(await readAccountReference());
});

export const finishAccountFn = createServerFn({ method: "POST" })
  .validator(nextInput.extend({ password: z.string().min(8).max(128) }))
  .handler(async ({ data }) => {
    const { finishAccount } = await import("~/lib/server/account-finish");
    const result = await finishAccount({
      ...(await readAccountReference()),
      password: data.password,
    });
    if (result.kind !== "ticket") return result;

    const { deleteCookie } = await import("@tanstack/react-start/server");
    const { COOKIE_NAMES } = await import("~/lib/cookies");
    deleteCookie(COOKIE_NAMES.accountLink, { path: "/" });
    return {
      kind: "ticket" as const,
      ticket: result.ticket,
      next: finishAccountDestination(data.next),
    };
  });

export const resendFinishAccountEmailFn = createServerFn({ method: "POST" })
  .validator(nextInput)
  .handler(async ({ data }): Promise<"sent" | "nothing_to_send"> => {
    const { resendFinishAccountEmail } =
      await import("~/lib/server/account-finish");
    return resendFinishAccountEmail({
      ...(await readAccountReference()),
      next: finishAccountDestination(data.next),
    });
  });

/**
 * Whether the signed-in user's account is unfinished: the score funnel made
 * it and she has not set its password. `/my-coaching` sends her to the finish
 * page while it is.
 */
export const getMyAccountUnfinishedFn = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .handler(async ({ context }): Promise<boolean> => {
    const { isAccountUnfinished } = await import("~/lib/server/account-finish");
    return isAccountUnfinished(context.user.id);
  });
